import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Player {
  id: string;
  name: string;
}

interface CreateGameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  players: Player[];
  onGameCreated: (gameId?: string) => void;
}

export const CreateGameDialog = ({ open, onOpenChange, groupId, players, onGameCreated }: CreateGameDialogProps) => {
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [buyinAmount, setBuyinAmount] = useState("");
  const [chipsPerBuyin, setChipsPerBuyin] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const buyin = parseFloat(buyinAmount);
    const chips = parseInt(chipsPerBuyin);
    
    if (!buyin || !chips || buyin <= 0 || chips <= 0) {
      toast({
        title: "Error",
        description: "Please enter valid buy-in amount and chips",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from('games')
        .insert({ 
          title: title.trim() || null,
          location: location.trim() || null,
          description: description.trim() || null,
          group_id: groupId,
          buyin_amount: buyin,
          chips_per_buyin: chips,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Game created successfully"
      });

      resetForm();
      onOpenChange(false);
      onGameCreated(data?.id);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create game",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setLocation("");
    setDescription("");
    setBuyinAmount("");
    setChipsPerBuyin("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Game</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Game Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Friday Night Poker"
            />
          </div>
          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., John's House"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Game notes or rules"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="buyinAmount">Buy-in Amount *</Label>
              <Input
                id="buyinAmount"
                type="number"
                step="0.01"
                min="0"
                value={buyinAmount}
                onChange={(e) => setBuyinAmount(e.target.value)}
                placeholder="1000"
                required
              />
            </div>
            <div>
              <Label htmlFor="chipsPerBuyin">Chips per Buy-in *</Label>
              <Input
                id="chipsPerBuyin"
                type="number"
                min="1"
                value={chipsPerBuyin}
                onChange={(e) => setChipsPerBuyin(e.target.value)}
                placeholder="10000"
                required
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !buyinAmount || !chipsPerBuyin}>
              {loading ? "Creating..." : "Create Game"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};